import type { GqlError, GqlResponse } from './gql-types'
import { HttpClientService } from './HttpClientService'
import type { OperationResult } from '../errors/OperationResult'
import * as op from '../errors/OperationResult'
import { AuthStatusService } from '@/core/auth/services/AuthStatusService.ts'
import { AuthService } from '@/core/auth/services/AuthService.ts'
import { normalizeIds } from '../gql-utils'
import { delaySeconds } from '../async-utils'

const gqlClientConfig = {
  simulateLatency: {
    createObject: 1.5,
    // viewSettings: 1,
    // collectionsTree: 2,
  },
}

export class GqlClientService {
  constructor(
    private readonly httpClientService: HttpClientService,
    private readonly authStatusService: AuthStatusService,
    private readonly authService: AuthService,
  ) {}

  public async sendGqlRequest<T>(
    query: string,
    options: {
      variables?: object
      logError?: boolean
      abortSignal?: AbortSignal
      skipAuth?: boolean
    } = {},
  ): Promise<OperationResult<GqlResponse<T>>> {
    return this.sendGqlRequestInternal<T>(query, options, false)
  }

  public formatGqlErrors(errors: GqlError[]): string {
    return errors.map((err) => this.formatGqlError(err)).join('\n')
  }

  private async sendGqlRequestInternal<T>(
    query: string,
    options: { variables?: object; logError?: boolean; abortSignal?: AbortSignal; skipAuth?: boolean },
    isRetry: boolean,
  ): Promise<OperationResult<GqlResponse<T>>> {
    const { variables, logError = true, abortSignal, skipAuth = false } = options

    const opRes = await this.httpClientService.sendRequest('/graphql', 'POST', {
      body: {
        query,
        variables,
      },
      headers: {
        'Content-Type': 'application/json',
      },
      logError,
      abortSignal,
      skipAuth,
    })
    await this.awaitSimulatedLatency(query)

    if (opRes.isError()) {
      return op.error(opRes.error)
    }

    const gqlRes = opRes.result as GqlResponse<T>

    if (gqlRes?.errors) {
      const errorStr = `error(s) in GQL response: ${this.formatGqlErrors(gqlRes.errors)}`

      if (logError) {
        console.error(errorStr)
      }

      if (!skipAuth && gqlRes.errors.some((err) => err.extensions?.code === 'UNAUTHENTICATED')) {
        if (!isRetry) {
          if (await this.authService.refresh()) {
            return this.sendGqlRequestInternal<T>(query, options, true)
          }
        }
        this.authStatusService.triggerAuthError()
      }

      const userMessage = gqlRes.errors.map((e) => e.message).join('\n')
      return op.error(userMessage)
    }

    normalizeIds(gqlRes.data)
    return op.success(gqlRes)
  }

  private async awaitSimulatedLatency(query: string) {
    if (gqlClientConfig.simulateLatency) {
      const match = Object.entries(gqlClientConfig.simulateLatency).find(([substring]) =>
        query.includes(substring),
      )
      if (match) {
        await delaySeconds(match[1])
      }
    }
  }

  private formatGqlError(err: GqlError): string {
    const code = err.extensions?.code ? `[${err.extensions.code}] ` : ''
    const path = err.path?.length ? ` @${err.path.join('.')}` : ''
    const stack = err.extensions?.stack ? `\nStack: ${err.extensions.stack}` : ''
    return `${code}${err.message}${path}${stack}`
  }
}
